import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useBrandingContext } from '../contexts/BrandingContext';
import SupportTicketForm from '../components/SupportTicketForm';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  SparklesIcon,
  CheckIcon,
  ArrowRightIcon,
  BriefcaseIcon,
  EyeIcon,
  ShieldCheckIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import PricingSection from './pricing/view/PricingSection';
import HeroSection from './demovideo/components/Demovideo';

const LandingPage: React.FC = () => {
  const { productName, branding } = useBrandingContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);


  const companyFeatures = [
    {
      icon: <SparklesIcon className="h-8 w-8" />,
      title: 'AI-Powered Resume Parsing',
      description: 'Automatically extract and analyze candidate information using advanced AI technology.'
    },
    {
      icon: <MagnifyingGlassIcon className="h-8 w-8" />,
      title: 'Smart Candidate Search',
      description: 'Find the perfect candidates with our intelligent search and matching algorithms.'
    },
    {
      icon: <UserGroupIcon className="h-8 w-8" />,
      title: 'Multi-Tenant Architecture',
      description: 'Secure, scalable platform designed for companies and individual candidates.'
    },
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      title: 'Advanced Analytics',
      description: 'Get insights into your recruitment process with detailed analytics and reports.'
    }
  ];

  const candidateFeatures = [
    {
      icon: <BriefcaseIcon className="h-8 w-8" />,
      title: 'AI Profile Creation',
      description: 'Upload your resume and let AI create a comprehensive professional profile automatically.'
    },
    {
      icon: <EyeIcon className="h-8 w-8" />,
      title: 'Get Discovered',
      description: 'Be found by top companies actively searching for talent like you.'
    },
    {
      icon: <ChartBarIcon className="h-8 w-8" />,
      title: 'Profile Analytics',
      description: 'Track who viewed your profile and get insights into your job search performance.'
    },
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: 'Verification Services',
      description: 'Stand out with optional ID verification and skill assessments for premium visibility.'
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>{productName} - {branding.productTagline}</title>
        <meta name="description" content="Streamline your recruitment process with AI-powered resume parsing, smart candidate search, and advanced analytics." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4 lg:py-6">
              <div className="flex items-center">
                <h1 className="text-xl sm:text-2xl font-bold text-gradient">{productName}</h1>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex space-x-8">
                <a href="#features" className="text-secondary-600 hover:text-primary-600 transition-colors">Features</a>
                <a href="#pricing" className="text-secondary-600 hover:text-primary-600 transition-colors">Pricing</a>
                <a href="#about" className="text-secondary-600 hover:text-primary-600 transition-colors">About</a>
                <button
                  onClick={() => setShowSupportForm(true)}
                  className="text-secondary-600 hover:text-primary-600 transition-colors"
                >
                  Contact Us
                </button>
              </nav>

              {/* Desktop CTA Buttons */}
              <div className="hidden lg:flex items-center space-x-4">
                <Link to="/auth/login" className="text-secondary-600 hover:text-primary-600 transition-colors">
                  Sign In
                </Link>
                <div className="flex items-center space-x-2">
                  <Link to="/auth/company-signup" className="btn btn-sm">
                    For Companies
                  </Link>
                  <Link to="/auth/candidate-signup" className="btn btn-sm bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600">
                    For Job Seekers
                  </Link>
                </div>
              </div>

              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <button
                  onClick={toggleMobileMenu}
                  className="p-2 rounded-md text-secondary-600 hover:text-primary-600 hover:bg-secondary-100 transition-colors"
                  aria-label="Toggle mobile menu"
                >
                  {isMobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="lg:hidden border-t border-secondary-200 py-4">
                <div className="flex flex-col space-y-4">
                  <a
                    href="#features"
                    className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                    onClick={closeMobileMenu}
                  >
                    Features
                  </a>
                  <a
                    href="#pricing"
                    className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                    onClick={closeMobileMenu}
                  >
                    Pricing
                  </a>
                  <a
                    href="#about"
                    className="text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                    onClick={closeMobileMenu}
                  >
                    About
                  </a>
                  <button
                    onClick={() => {
                      setShowSupportForm(true);
                      closeMobileMenu();
                    }}
                    className="text-left text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                  >
                    Contact Us
                  </button>
                  <div className="border-t border-secondary-200 pt-4 space-y-3">
                    <Link
                      to="/auth/login"
                      className="block text-secondary-600 hover:text-primary-600 transition-colors px-2 py-1"
                      onClick={closeMobileMenu}
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/auth/company-signup"
                      className="block btn btn-sm w-full text-center"
                      onClick={closeMobileMenu}
                    >
                      For Companies
                    </Link>
                    <Link
                      to="/auth/candidate-signup"
                      className="block btn btn-sm w-full text-center bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600"
                      onClick={closeMobileMenu}
                    >
                      For Job Seekers
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>
        <HeroSection />
        {/* Job Seeker Benefits Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-emerald-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary-900 mb-4">
                Why Job Seekers Love {productName}
              </h2>
              <p className="text-lg sm:text-xl text-secondary-600 max-w-2xl mx-auto">
                Join thousands of professionals who've advanced their careers with {productName}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12">
              {candidateFeatures.map((feature, index) => (
                <div key={index} className="card text-center bg-white border-emerald-200">
                  <div className="card-body p-6">
                    <div className="text-emerald-600 mb-4 flex justify-center">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-secondary-600 text-sm sm:text-base">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Job Seeker Benefits List */}
            <div className="bg-white rounded-lg p-6 sm:p-8 shadow-lg border border-emerald-200">
              <div className="grid grid-cols-1 gap-8">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-emerald-800 mb-4 flex items-center">
                    <CheckIcon className="h-6 w-6 mr-2 text-emerald-600" />
                    Free Features Included
                  </h3>
                  <ul className="space-y-3 text-secondary-700">
                    <li className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base">AI-powered profile creation from your resume</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Get discovered by companies actively hiring</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Track profile views and search analytics</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Smart skill extraction and matching</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Shareable public profile link</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Professional resume templates & downloads</span>
                    </li>
                    <li className="flex items-start">
                      <ShieldCheckIcon className="h-5 w-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base">Premium verification services available*</span>
                    </li>
                  </ul>
                  <p className="text-xs text-secondary-500 mt-4">
                    *Premium services like ID verification and background checks available for additional fees
                  </p>

                  <div className="mt-6">
                    <Link to="/auth/candidate-signup" className="btn w-full bg-emerald-600 text-white hover:bg-emerald-700">
                      Create Your Profile - Free to Start
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Features Section */}
        <section id="features" className="py-12 sm:py-16 lg:py-20 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary-900 mb-4">
                Powerful Features for Modern Recruitment
              </h2>
              <p className="text-lg sm:text-xl text-secondary-600 max-w-2xl mx-auto">
                Everything companies need to find, manage, and hire the best talent
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {companyFeatures.map((feature, index) => (
                <div key={index} className="card text-center">
                  <div className="card-body p-6">
                    <div className="text-primary-600 mb-4 flex justify-center">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-secondary-600 text-sm sm:text-base">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-secondary-900 mb-4">
                About {productName}
              </h2>
              <p className="text-lg sm:text-xl text-secondary-600 max-w-2xl mx-auto">
                Learn more about our mission and vision for revolutionizing talent acquisition
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-secondary-50 rounded-lg p-8 sm:p-12">
                <div className="prose prose-gray max-w-none mx-auto text-center">
                  <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                    {productName} is a modern talent acquisition and management platform built for the future of work.
                    We connect talented professionals with innovative companies through AI-powered matching and streamlined processes.
                  </p>
                  <p className="text-secondary-600 leading-relaxed">
                    Our platform combines advanced technology with human-centered design to make hiring more efficient,
                    fair, and effective for everyone involved.
                  </p>
                </div>
                <div className="text-center mt-8">
                  <Link to="/about" className="btn btn-primary">
                    Learn More About Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        < PricingSection />
        {/* CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Career or Hiring Process?
            </h2>
            <p className="text-lg sm:text-xl text-blue-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Join thousands of professionals and companies already using {productName} to connect talent with opportunity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
              <Link to="/auth/candidate-signup" className="btn btn-lg bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600 flex items-center justify-center">
                I'm Looking for Jobs
                <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Link>
              <Link to="/auth/company-signup" className="btn btn-lg bg-white text-primary-600 hover:bg-blue-50 flex items-center justify-center">
                I'm Hiring Talent
                <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-secondary-900 text-white py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              <div className="sm:col-span-2 lg:col-span-1">
                <h3 className="text-lg sm:text-xl font-bold text-gradient mb-4">{productName}</h3>
                <p className="text-secondary-300 text-sm sm:text-base">
                  Modern talent acquisition and management platform built for the future of work.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-4 text-sm sm:text-base">Product</h4>
                <ul className="space-y-2 text-secondary-300 text-sm">
                  <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                  <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4 text-sm sm:text-base">Company</h4>
                <ul className="space-y-2 text-secondary-300 text-sm">
                  <li><a href="/about" className="hover:text-white transition-colors">About</a></li>
                  <li>
                    <a href="/careers" className="hover:text-white transition-colors"> Careers </a>
                  </li>
                  <li>
                    <button
                      onClick={() => setShowSupportForm(true)}
                      className="hover:text-white transition-colors"
                    >
                      Contact
                    </button>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4 text-sm sm:text-base">Support</h4>
                <ul className="space-y-2 text-secondary-300 text-sm">
                  <li><a href="/help" className="hover:text-white transition-colors">Help Center</a></li>
                  <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                  <li><a href="/terms" className="hover:text-white transition-colors">Terms & Conditions</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-secondary-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-secondary-400">
              <p className="text-xs sm:text-sm">&copy; 2024 {productName}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Support Ticket Form Modal */}
      <SupportTicketForm
        isOpen={showSupportForm}
        onClose={() => setShowSupportForm(false)}
        source="LANDING_PAGE"
      />
    </>
  );
};

export default LandingPage; 
